import React, { useEffect, useState } from 'react';
import { fetchFolderDateEvidence, fetchFolderItems, type FolderDateEvidence, type FolderItems } from '../../services/projectFolderDateEvidence';
import { fetchStatusTextEvidence, statusTextPath, type StatusDateCandidate, type StatusTextEvidence } from '../../services/projectFolderDateEvidence';

type Props = {projectId:number; onReviewReceipt?:(isoDay:string)=>void; onReviewStatusDate?:(candidate:StatusDateCandidate)=>void};
const milestoneLabels = {receipt:'Files received', completeness:'Inputs ready', delivery:'Final delivery'};

function StatusText({projectId,onReviewStatusDate}: Props) {
  const [attempt,setAttempt] = useState(0);
  const [result,setResult] = useState<StatusTextEvidence|'error'|null>(null);
  useEffect(() => {
    let active = true;
    fetchStatusTextEvidence(projectId).then(
      data => { if (active) setResult(data); },
      () => { if (active) setResult('error'); }
    );
    return () => { active = false; };
  }, [projectId, attempt]);
  return <section aria-label="Project status text">
    <h4>Dates from PROJECT STATUS.txt</h4>
    <small>{statusTextPath}</small>
    {!result ? <p role="status">Reading project status text…</p> : result === 'error'
      ? <p role="status">Status text unavailable. <button type="button" onClick={() => { setResult(null); setAttempt(n => n+1); }}>Retry text</button></p>
      : <><small>Checked {result.checked}{result.modified && ' · File modified ' + result.modified}</small>
        {(Object.keys(milestoneLabels) as StatusDateCandidate['kind'][]).map(kind => {
          const candidates = result.candidates.filter(candidate => candidate.kind === kind);
          return <div key={kind}><strong>{milestoneLabels[kind]}</strong>
            {!candidates.length && <p>No explicit date in this text.</p>}
            {candidates.map((candidate,index) => <div key={index}><p>{candidate.day} · Pending your confirmation</p>
              <blockquote>{candidate.quote}</blockquote>
              {onReviewStatusDate && <button type="button" onClick={() => onReviewStatusDate(candidate)}>Review {milestoneLabels[kind].toLowerCase()} date</button>}
            </div>)}
          </div>;
        })}
      </>}
  </section>;
}
function FolderListing({projectId}:{projectId:number}) {
  const [path,setPath] = useState('');
  const [retry,setRetry] = useState(0);
  const [result,setResult] = useState<FolderItems|'error'|null>(null);
  useEffect(()=>{
    let active = true;
    setResult(null);
    fetchFolderItems(projectId,path).then(data=>{if(active) setResult(data);},()=>{if(active) setResult('error');});
    return ()=>{active=false;};
  },[projectId,path,retry]);
  const current = result && result !== 'error' && result.relativePath === path ? result : null;
  return <section aria-label="Mapped SharePoint folder listing">
    <h4>Project folder contents</h4>
    <p>SharePoint · {path || 'Mapped project root'} {path && <button type="button" onClick={()=>setPath(path.split('/').slice(0,-1).join('/'))}>Up one folder</button>}</p>
    {result === 'error' ? <p role="status">Folder listing unavailable. <button type="button" onClick={()=>setRetry(n=>n+1)}>Retry listing</button></p> :
      !current ? <p role="status">Reading folder contents…</p> : <>
        <small>Checked {current.checked}. This folder only · {current.partial ? 'Partial listing' : 'Immediate items listed'} · up to 25 items.</small>
        {!current.items.length ? <p>No visible items returned in this folder.</p> :
          <ul>{current.items.map(item=><li key={item.name}>{item.kind === 'folder' && path.split('/').filter(Boolean).length < 8 ?
            <button type="button" onClick={()=>setPath(path ? path + '/' + item.name : item.name)}>{item.name} /</button> : item.name}
            <small> · {item.kind} · {item.modified ? 'Modified ' + item.modified : 'Modified date unknown'}</small></li>)}</ul>}
        <small>File modification records activity. Confirm receipt and delivery dates separately.</small>
      </>}
  </section>;
}
function Evidence({projectId,onReviewReceipt,onReviewStatusDate}:Props) {
  const [opened,setOpened] = useState(false);
  const [retry,setRetry] = useState(0);
  const [result,setResult] = useState<FolderDateEvidence|'error'|null>(null);
  useEffect(() => {
    if (!opened) return;
    let active = true;
    setResult(null);
    fetchFolderDateEvidence(projectId).then(data => {if (active) setResult(data);}, () => {if (active) setResult('error');});
    return () => {active=false;};
  },[projectId, opened, retry]);
  return <details onToggle={event => {if (event.currentTarget.open) setOpened(true);}}>
    <summary>Project folder evidence</summary>
    {opened && <div>
      <StatusText projectId={projectId} onReviewStatusDate={onReviewStatusDate}/>
      <small>Source: 1-Project Documents / 7-AIPM / pmapp-project.json</small>
      {result === 'error' ? <p role="status">Published evidence unavailable. <button type="button" onClick={() => setRetry(n=>n+1)}>Retry</button></p>
        : !result ? <p role="status">Checking published dates…</p> : <>
          <p>Published {result.published}{result.stale ? ' · More than 24 hours old' : ''}</p>
          {result.description && <p><strong>Folder project description:</strong> {result.description}</p>}
          {result.intakeAvailable ? <dl>{([['Design documents received',result.received],['Design started',result.started],['Preliminary issue',result.preliminaryIssued]] as const).map(([label,date]) =>
            <div key={label}><dt>{label}</dt><dd>{date ?? 'Not recorded'}</dd></div>)}</dl> : <p>Design intake evidence unavailable in this publication.</p>}
          <small>Folder dates are evidence only. Confirm documents complete, commitment and final delivery separately in Today.</small>
          {onReviewReceipt && result.intakeAvailable && result.received && result.receivedDay && !result.stale &&
            <button type="button" onClick={() => onReviewReceipt(result.receivedDay!)}>Review receipt date</button>}
        </>}
      <FolderListing projectId={projectId}/>
    </div>}
  </details>;
}
export default function ProjectFolderDateEvidence(props:Props) {
  return <Evidence key={props.projectId} {...props}/>;
}
